import { useEffect, useState } from "react";
import { apiClient } from "@/context/AuthContext";
import { AlertTriangle, Check, Sparkles, X, Loader2 } from "lucide-react";

const PIN_TYPE = {
  exit: "exit", door: "exit", stair: "stair", fire_cabinet: "fire_cabinet", hydrant: "hydrant",
  sprinkler: "sprinkler", detector: "detector", assembly_point: "assembly_point", risk: "risk",
};
const hasXY = (s) => s.x !== null && s.x !== undefined && s.y !== null && s.y !== undefined;

function AiPreview({ img, suggestions }) {
  if (!img?.content_type?.startsWith("image/")) return null;
  return (
    <div className="ai-preview" data-testid="ai-preview">
      <img src={`data:${img.content_type};base64,${img.data_b64}`} alt="kroki"/>
      {suggestions.filter(hasXY).map((s) => (
        <span key={s.id} className={`ai-marker ${s.verified ? (s.accepted ? "ok" : "bad") : ""}`} style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%` }} title={s.label} data-testid={`ai-marker-${s.id}`}>
          {suggestions.indexOf(s) + 1}
        </span>
      ))}
    </div>
  );
}

export default function PlanAiPanel({ plan, assessmentId, onClose, onToast }) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [img, setImg] = useState(null);

  const runAnalysis = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await apiClient.post(`/plans/${plan.id}/analyze`, { focus: "general" });
      setSuggestions((rows) => [...(data.suggestions || []), ...rows.filter((r) => r.verified)]);
      setNotes(data.notes || "");
      onToast(`${(data.suggestions || []).length} AI önerisi geldi`);
    } catch (err) {
      setError(err?.response?.data?.detail || "AI analizi başarısız");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [s, i] = await Promise.allSettled([
        apiClient.get(`/plans/${plan.id}/suggestions`), apiClient.get(`/plans/${plan.id}/image`),
      ]);
      if (s.status === "fulfilled") setSuggestions(s.value.data || []);
      if (i.status === "fulfilled") setImg(i.value.data);
    })();
  }, [plan.id]);

  const verify = async (s, action) => {
    try {
      await apiClient.post(`/plans/${plan.id}/suggestions/${s.id}/verify`, { action });
      setSuggestions((rows) => rows.map((r) => r.id === s.id ? { ...r, verified: true, accepted: action === "confirm" } : r));
      if (action === "confirm" && hasXY(s)) {
        await apiClient.post(`/plans/${plan.id}/pins`, {
          type: PIN_TYPE[s.type] || "custom", label: s.label || s.type, x: s.x, y: s.y,
          source_rule: s.source_rule || "", note: "AI önerisi · danışman doğruladı",
        });
        onToast("AI önerisi doğrulandı ve krokiye pin olarak eklendi");
      } else {
        onToast(action === "confirm" ? "AI önerisi kabul edildi" : "AI önerisi reddedildi");
      }
    } catch { onToast("Doğrulama kaydedilemedi"); }
  };

  return (
    <div className="modal-backdrop" data-testid="ai-panel">
      <div className="modal wide-modal">
        <div className="modal-head">
          <div><div className="eyebrow">AI KROKİ ANALİZİ · Gemini 3.1 Pro</div><h2>{plan.file_name}</h2></div>
          <button type="button" className="icon-button" onClick={onClose} data-testid="close-ai-panel"><X size={17}/></button>
        </div>
        <div className="ai-warning">
          <AlertTriangle size={16}/>
          <span>AI önerileri yalnızca yardımcı niteliktedir; her öğeyi onaylamadan rapora alınmaz. Onaylanan konumlu öneriler krokiye pin olarak eklenir.</span>
        </div>
        <button className="primary-btn" onClick={runAnalysis} disabled={loading} data-testid="run-ai-analysis">
          {loading ? <><Loader2 size={16} className="spin"/> Gemini krokiyi inceliyor… (~20 sn)</> : <><Sparkles size={16}/> Yeni analiz başlat</>}
        </button>
        {error && <div className="auth-error" style={{marginTop: 14}}><AlertTriangle size={14}/> {error}</div>}
        {notes && <small className="legal-foot" style={{marginTop: 10}}>Not: {notes}</small>}
        <AiPreview img={img} suggestions={suggestions}/>
        <div className="ai-list" data-testid="ai-suggestions-list">
          {suggestions.map((s, i) => (
            <article key={s.id} className={`ai-row ${s.verified ? (s.accepted ? "ok" : "bad") : ""}`} data-testid={`ai-sug-${s.id}`}>
              <div className="ai-main">
                <div className="ai-head"><strong><span className="ai-idx">{i + 1}</span>{s.label || s.type}</strong><span>{s.type}</span></div>
                <small>{s.location || "konum belirsiz"} · güven {(Number(s.confidence) * 100 || 0).toFixed(0)}%</small>
                {s.source_rule && <small>{s.source_rule}</small>}
              </div>
              {!s.verified ? (
                <div className="finding-actions">
                  <button className="confirm" onClick={() => verify(s, "confirm")} data-testid={`ai-confirm-${s.id}`}><Check size={14}/> Doğrula</button>
                  <button className="reject" onClick={() => verify(s, "reject")} data-testid={`ai-reject-${s.id}`}><X size={14}/></button>
                </div>
              ) : (
                <span className={s.accepted ? "status-good" : "status-bad"}>{s.accepted ? "Kabul edildi" : "Reddedildi"}</span>
              )}
            </article>
          ))}
          {!suggestions.length && !loading && <small className="ai-empty">Henüz öneri yok. Analizi başlatın.</small>}
        </div>
        {!assessmentId && <small className="legal-foot" style={{marginTop: 12}}>Not: Önceden bir değerlendirme oluşturursanız öneriler ilgili bulgularla eşlenebilir.</small>}
      </div>
    </div>
  );
}
